import React from 'react';

/**
 * DsButton — ボタンの正規実装（Web）。
 *
 * 新規・改修のボタンは原則これを使う。既存の共通CSS基盤（.btn / components.css）が
 * 付与するフラットな見た目・色ロール・状態（hover/disabled/タップ44px）をそのまま
 * 継承するため、bespoke な `xxx-btn` クラスを新設しない（＝デザインシステム順守）。
 *
 * variant: 'create'(追加・新規作成・青) | 'modify'(変更・保存・緑)
 *          | 'primary'(主・緑/既定) | 'info'(情報操作・青) | 'secondary'(副・グレー) | 'danger'(危険・赤)
 *          | 'success'/'neutral'/'ghost'（既存 .btn 移行用 alias）
 * size:    'sm' | 'md'(既定) | 'lg'
 * appearance: 'filled'(既定) | 'text'（背景・枠のない補助操作）
 * block:   true で幅100%
 * icon:    true で正方形アイコンボタン（aria-label を必ず渡す）
 * loading: true で処理中（操作不可＋スピナー、aria-busy）
 *
 * type は既定 'button'（フォーム誤送信を防ぐ）。onClick / aria-* / form などの
 * 属性はそのまま下の要素へ委譲。`as` に 'a' 等を渡すとリンクとして同じ見た目で描画。
 *
 * 色・角丸・状態の正は docs/DEVELOPMENT.md（§2.5 早見表 / §2.8 部品ファースト）。
 */
const VARIANT_CLASS = {
  create: 'btn--create',
  modify: 'btn--modify',
  primary: 'btn--primary',
  info: 'btn--info',
  secondary: 'btn--secondary',
  danger: 'btn--danger',
  success: 'btn--success',
  neutral: 'btn--neutral',
  ghost: 'btn--ghost',
};

const SIZE_CLASS = {
  sm: 'btn--sm',
  md: '',
  lg: 'btn--lg',
};

const CREATE_ACTION_PATTERN = /(追加|新規.*(?:作成|登録)|(?:作成|登録|発行|招待|申請|生成|アップロード|インポート|取り込)(?:する|$|へ))/;
const CREATE_ACTION_EXCLUSION_PATTERN = /(戻る|中止|キャンセル|へ進む|作成ページ|保持・削除)/;
const DANGER_ACTION_PATTERN = /^(削除|破棄|下書きを破棄|変更を破棄)$/;

const buttonText = (children) => React.Children.toArray(children)
  .filter((child) => typeof child === 'string' || typeof child === 'number')
  .join(' ')
  .replace(/\s+/g, ' ')
  .trim();

export const resolveDsButtonVariant = (variant, children) => {
  if (variant) return variant;
  const label = buttonText(children);
  if (DANGER_ACTION_PATTERN.test(label)) return 'danger';
  if (!CREATE_ACTION_EXCLUSION_PATTERN.test(label) && CREATE_ACTION_PATTERN.test(label)) return 'create';
  return 'primary';
};

/**
 * @param {{
 *   variant?: 'create'|'modify'|'primary'|'info'|'secondary'|'danger'|'success'|'neutral'|'ghost',
 *   size?: 'sm'|'md'|'lg',
 *   appearance?: 'filled'|'text',
 *   block?: boolean,
 *   icon?: boolean,
 *   loading?: boolean,
 *   disabled?: boolean,
 *   type?: string,
 *   as?: React.ElementType,
 *   className?: string,
 *   children?: React.ReactNode,
 *   [key: string]: any,
 * }} props
 */
export default function DsButton({
  variant,
  size = 'md',
  appearance = 'filled',
  block = false,
  icon = false,
  loading = false,
  disabled = false,
  type = 'button',
  as: As = 'button',
  className = '',
  children,
  onClick,
  ...rest
}) {
  const resolvedVariant = resolveDsButtonVariant(variant, children);
  const classes = [
    'btn',
    VARIANT_CLASS[resolvedVariant] || VARIANT_CLASS.primary,
    SIZE_CLASS[size] || '',
    appearance === 'text' ? 'btn--text' : '',
    block ? 'btn--block' : '',
    icon ? 'btn--icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const isDisabled = disabled || loading;

  // button のときだけ type/disabled を渡す。a 等では無効属性のため aria で表現。
  const elementProps =
    As === 'button'
      ? { type, disabled: isDisabled }
      : {
          'aria-disabled': isDisabled || undefined,
          ...(isDisabled ? { tabIndex: -1 } : {}),
          ...(As === 'a' && isDisabled ? { href: undefined } : {}),
        };

  const handleClick = (event) => {
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  };

  return (
    <As
      className={classes}
      data-loading={loading ? '' : undefined}
      aria-busy={loading || undefined}
      {...rest}
      {...elementProps}
      onClick={handleClick}
    >
      {children}
    </As>
  );
}
